"use client";

// The company page's section ticker — the navigator grammar, 1:1 with
// OsNavigator's sibling strip: the active word slides to the FAR LEFT in full
// ink; the next words trail right, faded; words that pass the left edge blur +
// fade out and a wrapped copy rides behind so they REAPPEAR from the right — an
// infinite-feeling rotation, so every option is always reachable. Drag/swipe
// (mobile AND desktop) steps between sections with wrap-around; tapping a word
// selects it. Same 44px commit threshold, same 0.55s ease-drift glide, same
// exit blur/fade math, and the same hover (law_nav_proximity): near the rail
// every word comes up to ~.5, the one under the pointer to ~.8 (`.os-sib-word`
// in globals.css, lit by the global HoverField tracker).

import { useEffect, useLayoutEffect, useRef, useState } from "react";

const useIso = typeof window !== "undefined" ? useLayoutEffect : useEffect;

export default function CompanyTicker({
  tabs,
  active,
  onSelect,
}: {
  tabs: readonly { key: string; label: string }[];
  active: string;
  onSelect: (key: string) => void;
}) {
  const n = tabs.length;
  const activeIdx = Math.max(0, tabs.findIndex((t) => t.key === active));
  const railRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [offsets, setOffsets] = useState<number[]>([]);
  const [dragDx, setDragDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragState = useRef<{ x0: number; moved: boolean; pid: number } | null>(null);
  const didDragRef = useRef(false);
  const keysSig = tabs.map((t) => t.key).join("|");

  // measure each first-copy item's left offset so the strip can translate the
  // active word to the far-left edge.
  useIso(() => {
    const measure = () => setOffsets(itemRefs.current.slice(0, n).map((el) => el?.offsetLeft ?? 0));
    measure();
    const ro = new ResizeObserver(measure);
    if (railRef.current) ro.observe(railRef.current);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n, keysSig]);

  const translateX = -(offsets[activeIdx] ?? 0) + dragDx;

  // wrap-around step — from the last word, one more step returns to the first.
  const step = (dir: 1 | -1) => {
    if (n < 2) return;
    const next = (activeIdx + dir + n) % n;
    onSelect(tabs[next].key);
  };

  const onDown = (e: React.PointerEvent) => {
    dragState.current = { x0: e.clientX, moved: false, pid: e.pointerId };
    didDragRef.current = false;
  };
  const onMove = (e: React.PointerEvent) => {
    const d = dragState.current;
    if (!d) return;
    const dx = e.clientX - d.x0;
    if (!d.moved && Math.abs(dx) > 4) {
      d.moved = true;
      didDragRef.current = true;
      setDragging(true);
      try { railRef.current?.setPointerCapture(d.pid); } catch { /* ignore */ }
    }
    if (d.moved) setDragDx(dx);
  };
  const onUp = () => {
    const d = dragState.current;
    dragState.current = null;
    if (d?.moved) {
      const THRESH = 44; // px of travel to commit a section change
      if (dragDx <= -THRESH) step(1);
      else if (dragDx >= THRESH) step(-1);
      setTimeout(() => { didDragRef.current = false; }, 0);
    }
    setDragDx(0);
    setDragging(false);
  };

  return (
    <div
      ref={railRef}
      data-hl-near="28"
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerLeave={onUp}
      style={{
        overflow: "hidden",
        padding: "0.5rem 0",
        overscrollBehaviorX: "contain",
        WebkitMaskImage: "linear-gradient(90deg, #000 0, #000 80%, transparent 100%)",
        maskImage: "linear-gradient(90deg, #000 0, #000 80%, transparent 100%)",
        cursor: dragging ? "grabbing" : "grab",
        touchAction: "pan-y",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: "clamp(1.4rem, 3vw, 3.2rem)",
          width: "max-content",
          transform: `translateX(${translateX}px)`,
          transition: dragging ? "none" : "transform 0.5s var(--ease-glide)",
          willChange: "transform",
        }}
      >
        {/* Every word shows EXACTLY ONCE. As the active word eases to the far
            left, only the words that have actually passed off the LEFT edge
            reappear — wrapped — at the right (OsNavigator's rotation). */}
        {[...tabs, ...tabs.slice(0, activeIdx)].map((t, gi) => {
          const wrapped = gi >= n; // the reappear-on-the-right copy of an exited word
          const isActivePos = gi === activeIdx;
          const rel = gi - activeIdx; // < 0 → first copy that has slid off the left
          const blur = rel < 0 && !wrapped ? Math.min(7, -rel * 3.2) : 0;
          const past = rel < 0 && !wrapped; // slid off the left — keeps its blur + fade
          const op = past ? Math.max(0, 0.55 + rel * 0.2) : 1;
          return (
            <button
              key={`${t.key}-${wrapped ? "w" : "a"}`}
              ref={(el) => { if (gi < n) itemRefs.current[gi] = el; }}
              type="button"
              onClick={() => { if (!didDragRef.current) onSelect(t.key); }}
              className="os-sib-word"
              data-hl-target=""
              data-active={isActivePos ? "" : undefined}
              data-past={past ? "" : undefined}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                fontFamily: "inherit",
                whiteSpace: "nowrap",
                flexShrink: 0,
                letterSpacing: "-0.025em",
                lineHeight: 1.15,
                fontSize: "var(--os-title, clamp(1.9rem, 1.4rem + 2vw, 2.7rem))",
                // colour + opacity are CSS's (.os-sib-word); the depth blur
                // steps, never tweens (law_snappy).
                filter: blur ? `blur(${blur}px)` : undefined,
                ["--nw-op" as string]: op,
              } as React.CSSProperties}
            >
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
